import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { extractErrorMessage } from '../../utils/errorUtils';
import { formatMoney } from '../reports/formatters';
import { useGetOptionsQuery, useLazyGenerateReportQuery } from './api/bankAccountBalanceReportApi';
import type {
  BankAccountBalanceReportCompanyOption,
  BankAccountBalanceReportRequest,
  BankAccountBalanceReportRow,
} from './types';

const today = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const createRequest = (
  reportDate: string,
  companyId: number | null
): BankAccountBalanceReportRequest => ({
  ReportDate: reportDate,
  CompanyId: companyId,
});

const amount = (value: number | null | undefined, currency?: string) =>
  value == null ? '-' : `${formatMoney(value)}${currency ? ` ${currency}` : ''}`;

const ReportRow = ({ row }: { row: BankAccountBalanceReportRow }) => (
  <TableRow>
    <TableCell>{row.companyName}</TableCell>
    <TableCell>{row.bankName || '-'}</TableCell>
    <TableCell>
      {row.accountName || '-'}
      {row.currency ? ` / ${row.currency}` : ''}
    </TableCell>
    <TableCell align="right">{amount(row.openingBalance, row.currency)}</TableCell>
    <TableCell align="right">{amount(row.income, row.currency)}</TableCell>
    <TableCell align="right">{amount(row.expense, row.currency)}</TableCell>
    <TableCell align="right">{amount(row.closingBalance, row.currency)}</TableCell>
    <TableCell align="right">{amount(row.openingBalanceEur, 'EUR')}</TableCell>
    <TableCell align="right">{amount(row.closingBalanceEur, 'EUR')}</TableCell>
  </TableRow>
);

export const BankAccountBalanceReportPage = () => {
  const { data: options, isLoading: optionsLoading, error: optionsError } = useGetOptionsQuery();
  const [generateReport, { data: report, isFetching: reportLoading, error: reportError, reset }] =
    useLazyGenerateReportQuery();
  const [reportDate, setReportDate] = useState(today);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const initialized = useRef(false);
  const companies = useMemo(() => options?.companies ?? [], [options?.companies]);
  const selectedCompany = companies.find((company) => company.id === companyId) ?? null;
  const isLoading = optionsLoading || reportLoading;

  useEffect(() => {
    if (initialized.current || optionsLoading) return;
    initialized.current = true;
    void generateReport(createRequest(reportDate, null));
  }, [generateReport, optionsLoading, reportDate]);

  const generate = () => void generateReport(createRequest(reportDate, companyId));
  const clear = () => {
    setReportDate(today());
    setCompanyId(null);
    reset();
  };

  return (
    <Box
      component="section"
      aria-label="Bank Accounts Balance Report"
      sx={{ p: { xs: 1, md: 2 }, height: '100%', overflow: 'auto' }}
    >
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <TextField
            fullWidth
            label="Date"
            type="date"
            value={reportDate}
            onChange={(event) => setReportDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={isLoading}
          />
          <Autocomplete
            fullWidth
            options={companies}
            value={selectedCompany}
            onChange={(_event, value) => setCompanyId(value?.id ?? null)}
            getOptionLabel={(option: BankAccountBalanceReportCompanyOption) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={isLoading}
            renderInput={(params) => (
              <TextField {...params} label="Company" placeholder="All companies" />
            )}
          />
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button variant="contained" onClick={generate} disabled={isLoading || !reportDate}>
              {reportLoading ? 'Loading...' : 'Generate Report'}
            </Button>
            <Button variant="outlined" onClick={clear} disabled={isLoading}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {(optionsError || reportError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractErrorMessage(
            reportError || optionsError,
            'Failed to load bank account balance report.'
          )}
        </Alert>
      )}
      {report?.warnings.map((warning) => (
        <Alert key={warning} severity="warning" sx={{ mb: 1 }}>
          {warning}
        </Alert>
      ))}
      {isLoading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {report && (
        <Paper variant="outlined">
          <Box sx={{ p: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Cash position for {report.reportDate.slice(0, 10)}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Account / Currency</TableCell>
                  <TableCell align="right">Opening Balance</TableCell>
                  <TableCell align="right">Income</TableCell>
                  <TableCell align="right">Expense</TableCell>
                  <TableCell align="right">Closing Balance</TableCell>
                  <TableCell align="right">Opening Balance EUR</TableCell>
                  <TableCell align="right">Closing Balance EUR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.rows.length > 0 ? (
                  report.rows.map((row) => <ReportRow key={row.id} row={row} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover', p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Totals EUR
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Typography>Opening: {amount(report.totals.openingBalanceEur, 'EUR')}</Typography>
              <Typography>Income: {amount(report.totals.incomeEur, 'EUR')}</Typography>
              <Typography>Expense: {amount(report.totals.expenseEur, 'EUR')}</Typography>
              <Typography>Closing: {amount(report.totals.closingBalanceEur, 'EUR')}</Typography>
            </Stack>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
